import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  Icon, 
  Heading, 
  Grid,
  GridItem,
  VStack,
  HStack,
  Circle,
  Divider,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  AlertTitle,
  Tooltip,
  Tab,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  FormControl,
  FormLabel,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  UnorderedList,
  ListItem
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, 
  DollarSign, 
  Users, 
  Clock,
  Heart,
  Activity,
  BarChart,
  AlertTriangle,
  Info,
  Percent
} from 'lucide-react';
import NeuralBackground from '../../components/NeuralBackground';
import SlideContainer from '../../components/SlideContainer';
import { MethodistSlide } from '../../components/base/MethodistSlide';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
} from 'recharts';

// ROI metric component
const ROIMetric: React.FC<{
  title: string;
  value: string;
  description: string;
  icon: React.FC;
  color: string;
  index: number;
}> = ({ title, value, description, icon, color, index }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      style={{ width: '100%' }}
    >
      <Box 
        bg={bgColor} 
        p={4} 
        borderRadius="lg" 
        boxShadow="md" 
        borderWidth="1px" 
        borderColor={borderColor}
        height="100%"
      >
        <Flex align="center" mb={3}>
          <Circle size="40px" bg={`${color}15`} mr={3}>
            <Icon as={icon} color={color} boxSize={5} />
          </Circle>
          <Text fontWeight="bold" fontSize="sm">{title}</Text>
        </Flex>
        <Text fontSize="2xl" fontWeight="bold" mb={2} color={color}>
          {value}
        </Text>
        <Text fontSize="xs" color="gray.500">
          {description}
        </Text>
      </Box>
    </motion.div>
  );
};

// Helper function to generate a random number within a range
const getRandomInRange = (min: number, max: number) => {
  return Math.random() * (max - min) + min;
};

const ROISlide: React.FC = () => {
  // Colors
  const methodistBlue = "#0066CC";
  const methodistGreen = "#38A169";
  
  // Theme colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  
  // State for simulation
  const [adoptionRate, setAdoptionRate] = useState<number>(15);
  const [burnoutReduction, setBurnoutReduction] = useState<number>(10);
  const [platformCost, setPlatformCost] = useState<number>(5000);
  const [simulationResults, setSimulationResults] = useState<{ name: string; color: string; results: any }[]>([]);
  
  // Calculate yearly savings based on adoption rate
  const calculateYearlySavings = useCallback((adoptionRate: number) => {
    const totalUsers = 3000; // Total potential users
    const averagePhysicianSalary = 300000; // Average physician salary
    const burnoutTurnoverMultiplier = 2.5; // Average cost is 2.5x annual salary for turnover
    const physicianBurnoutCostPerDoctor = averagePhysicianSalary * burnoutTurnoverMultiplier; // Cost of burnout per physician
    const burnoutBaseline = 0.54; // 54% baseline burnout rate (Medscape 2023)
    const severeBurnoutRate = 0.33; // ~33% of burnout cases lead to turnover/severe productivity loss
    
    // Calculate number of users adopted
    const usersAdopted = Math.round(totalUsers * (adoptionRate / 100));
    
    // Calculate burnout reduction
    const burnoutAfter = burnoutBaseline * (1 - (burnoutReduction / 100));
    const burnoutReduced = burnoutBaseline - burnoutAfter;
    
    // Calculate doctors saved from severe burnout
    const doctorsSaved = Math.round(usersAdopted * burnoutReduced * severeBurnoutRate);
    
    // Calculate cost savings
    return doctorsSaved * physicianBurnoutCostPerDoctor;
  }, [burnoutReduction]);
  
  // Run Monte Carlo simulation inside component
  const runMonteCarloSimulation = useCallback(() => {
    // Fixed parameters
    const averagePhysicianSalary = 300000; // Average physician salary
    const burnoutTurnoverMultiplier = 2.5; // Average cost is 2.5x annual salary for turnover
    const physicianBurnoutCostPerDoctor = averagePhysicianSalary * burnoutTurnoverMultiplier; // Cost of burnout per physician
    const totalPotentialUsers = 3000; // Total potential users
    const iterations = 1000; // Number of iterations
    const burnoutBaseline = 0.54; // 54% baseline burnout rate (Medscape 2023)
    const severeBurnoutRate = 0.33; // ~33% of burnout cases lead to turnover/severe productivity loss
    
    // Results accumulator
    let totalDoctorsSaved = 0;
    let totalCostSavings = 0;
    let totalROI = 0;
    
    // Run simulation iterations
    for (let i = 0; i < iterations; i++) {
      // Vary the parameters slightly for each iteration to simulate uncertainty
      const actualAdoptionRate = getRandomInRange(adoptionRate * 0.9, adoptionRate * 1.1) / 100;
      const actualBurnoutReduction = getRandomInRange(burnoutReduction * 0.8, burnoutReduction * 1.2) / 100;
      const actualBurnoutCost = getRandomInRange(physicianBurnoutCostPerDoctor * 0.8, physicianBurnoutCostPerDoctor * 1.2);
      
      // Calculate results for this iteration
      const usersAdopted = Math.round(totalPotentialUsers * actualAdoptionRate);
      const burnoutAfter = burnoutBaseline * (1 - actualBurnoutReduction);
      const burnoutReduced = burnoutBaseline - burnoutAfter;
      
      // Calculate doctors saved (reduced burnout * number of users adopted)
      const doctorsSaved = Math.round(usersAdopted * burnoutReduced * severeBurnoutRate);
      
      // Calculate cost savings (doctors saved * cost per burnout)
      const costSavings = doctorsSaved * actualBurnoutCost;
      
      // Calculate ROI (cost savings / platformCost * 100)
      const roi = (costSavings / platformCost) * 100;
      
      // Accumulate results
      totalDoctorsSaved += doctorsSaved;
      totalCostSavings += costSavings;
      totalROI += roi;
    }
    
    // Calculate averages
    const avgDoctorsSaved = Math.round(totalDoctorsSaved / iterations);
    const avgCostSavings = Math.round(totalCostSavings / iterations);
    const avgROI = Math.round(totalROI / iterations);
    
    // Calculate 5-year projection
    const fiveYearSavings = avgCostSavings * 5;
    
    // Calculate confidence interval (simplified version)
    const confidenceMin = Math.round(avgROI * 0.8);
    const confidenceMax = Math.round(avgROI * 1.2);
    
    // Calculate payback period in months
    const paybackMonths = platformCost / (avgCostSavings / 12);
    
    return {
      adoptionRate: adoptionRate,
      burnoutReduction: burnoutReduction,
      usersAdopted: Math.round(totalPotentialUsers * (adoptionRate / 100)),
      doctorsSaved: avgDoctorsSaved,
      costSavings: avgCostSavings,
      fiveYearSavings: fiveYearSavings,
      roi: avgROI,
      confidenceInterval: `${confidenceMin}%-${confidenceMax}%`,
      paybackPeriod: Math.round(paybackMonths)
    };
  }, [adoptionRate, burnoutReduction, platformCost]);
  
  // Update adoption scenarios when parameters change
  const adoptionScenarios = useMemo(() => [
    {
      name: "Conservative",
      color: "#4299E1", // blue.400
      strokeDasharray: "0", // solid line
      rates: [Math.max(1, Math.floor(adoptionRate * 0.5)), Math.max(2, Math.floor(adoptionRate * 0.7)), Math.max(5, Math.floor(adoptionRate * 0.9)), Math.max(7, Math.floor(adoptionRate * 1.1)), Math.max(10, Math.floor(adoptionRate * 1.2))]
    },
    {
      name: "Moderate",
      color: "#38A169", // green.500
      strokeDasharray: "5 5", // dashed line
      rates: [adoptionRate, Math.floor(adoptionRate * 1.2), Math.floor(adoptionRate * 1.5), Math.floor(adoptionRate * 1.7), Math.floor(adoptionRate * 2)]
    },
    {
      name: "Optimistic",
      color: "#805AD5", // purple.500
      strokeDasharray: "2 2", // dotted line
      rates: [Math.floor(adoptionRate * 1.2), Math.floor(adoptionRate * 1.5), Math.floor(adoptionRate * 2), Math.floor(adoptionRate * 2.5), Math.min(99, Math.floor(adoptionRate * 3))]
    }
  ], [adoptionRate]);

  // Prepare data for Recharts
  const chartData = useMemo(() => {
    return [0, 1, 2, 3, 4].map(yearIndex => {
      const dataPoint = {
        name: `Year ${yearIndex + 1}`,
        year: yearIndex + 1,
      };
      
      adoptionScenarios.forEach(scenario => {
        const savings = calculateYearlySavings(scenario.rates[yearIndex]);
        dataPoint[scenario.name] = Math.round(savings / 1000); // Convert to K
      });
      
      return dataPoint;
    });
  }, [adoptionScenarios, calculateYearlySavings]);

  // Calculate max value for Y axis
  const maxValue = useMemo(() => {
    const maxSavings = calculateYearlySavings(Math.min(99, adoptionRate * 3));
    const withPlatformCost = Math.max(maxSavings, platformCost);
    return Math.ceil(withPlatformCost / 1000); // Convert to K and round up
  }, [calculateYearlySavings, adoptionRate, platformCost]);

  // Update simulation when parameters change
  useEffect(() => {
    // Create simulation results for different adoption rates
    const results = adoptionScenarios.map(scenario => {
      return {
        name: scenario.name,
        color: scenario.color,
        results: runMonteCarloSimulation()
      };
    });
    
    setSimulationResults(results);
  }, [adoptionRate, burnoutReduction, platformCost, runMonteCarloSimulation, adoptionScenarios]);
  
  // Adoption rate scenarios for the table
  const adoptionRates = [5, 10, 25, 50]; // in percentages
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Format percentage
  const formatPercentage = (value: number | undefined) => {
    if (value === undefined) return "0%";
    return `${value.toLocaleString()}%`;
  };
  
  // ROI metrics
  const roiMetrics = [
    {
      title: "ROI Ratio",
      value: "3.2x",
      description: "Conservative 3-year return on investment",
      icon: TrendingUp,
      color: methodistGreen
    },
    {
      title: "Payback Period",
      value: "6 months",
      description: "Based on conservative adoption rates",
      icon: Clock,
      color: methodistBlue
    },
    {
      title: "Net Present Value",
      value: "$11K",
      description: "3-year NPV with 10% discount rate",
      icon: DollarSign,
      color: "#DD6B20" // Orange
    },
    {
      title: "Clinician Retention",
      value: "+15%",
      description: "Conservative improvement in retention",
      icon: Users,
      color: "#805AD5" // Purple
    }
  ];
  
  // Financial benefits
  const financialBenefits = [
    { 
      category: "Reduced Turnover Costs", 
      year1: 2500, 
      year2: 4000, 
      year3: 5500, 
      total: 12000 
    },
    { 
      category: "Improved Productivity", 
      year1: 0, 
      year2: 1800, 
      year3: 3200, 
      total: 5000 
    },
    { 
      category: "Reduced Burnout-Related Absences", 
      year1: 750, 
      year2: 1250, 
      year3: 1750, 
      total: 3750 
    },
    { 
      category: "Resource Optimization", 
      year1: 500, 
      year2: 1200, 
      year3: 1800, 
      total: 3500 
    },
    { 
      category: "Implementation Costs", 
      year1: -5000, 
      year2: -1000, 
      year3: -750, 
      total: -6750 
    },
    { 
      category: "Total Benefits", 
      year1: 3750, 
      year2: 7250, 
      year3: 11500, 
      total: 22500,
      isTotal: true
    }
  ];
  
  // Non-financial benefits
  const nonFinancialBenefits = [
    {
      metric: "Self-Care Engagement",
      value: "+30%",
      impact: "Increased utilization of wellness resources and self-care practices",
      icon: Heart,
      color: "#E53E3E" // Red
    },
    {
      metric: "Resource Coordination",
      value: "+25%",
      impact: "Improved efficiency in connecting clinicians with appropriate resources",
      icon: Activity,
      color: methodistGreen
    },
    {
      metric: "Targeted Interventions",
      value: "+40%",
      impact: "More precise delivery of interventions to clinicians who need them most",
      icon: BarChart,
      color: methodistBlue
    },
    {
      metric: "Work-Life Balance",
      value: "+20%",
      impact: "Improved satisfaction with work-life integration and personal time",
      icon: Users,
      color: "#805AD5" // Purple
    }
  ];
  
  return (
    <MethodistSlide title="Return on Investment">
      <SlideContainer>
        <Box position="relative" height="100%" overflow="hidden">
          <Box position="absolute" top={0} right={0} bottom={0} left={0} zIndex={-1} opacity={0.05}>
            <NeuralBackground />
          </Box>
          
          <Tabs variant="enclosed" colorScheme="blue" size="sm">
            <TabList>
              <Tab fontWeight="medium">ROI Summary</Tab>
              <Tab fontWeight="medium">5-Year Projections</Tab>
            </TabList>
            
            <TabPanels>
              {/* ROI Summary Tab */}
              <TabPanel p={0}>
                <Flex direction="column" height="100%" p={6}>
                  {/* ROI Metrics */}
                  <Box mb={6}>
                    <Heading size="sm" mb={4} color={methodistBlue}>
                      ROI Metrics
                    </Heading>
                    <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} gap={4}>
                      {roiMetrics.map((metric, index) => (
                        <ROIMetric
                          key={metric.title}
                          title={metric.title}
                          value={metric.value}
                          description={metric.description}
                          icon={metric.icon}
                          color={metric.color}
                          index={index}
                        />
                      ))}
                    </Grid>
                  </Box>
                  
                  {/* Financial Benefits */}
                  <Box mb={6}>
                    <Heading size="sm" mb={4} color={methodistBlue}>
                      Financial Benefits (3-Year Projection)
                    </Heading>
                    <Box 
                      bg={bgColor} 
                      borderRadius="lg" 
                      boxShadow="md" 
                      borderWidth="1px" 
                      borderColor={borderColor}
                      overflow="hidden"
                    >
                      <Table size="sm" variant="simple">
                        <Thead bg={`${methodistBlue}10`}>
                          <Tr>
                            <Th>Category</Th>
                            <Th isNumeric>Year 1</Th>
                            <Th isNumeric>Year 2</Th>
                            <Th isNumeric>Year 3</Th>
                            <Th isNumeric>Total</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {financialBenefits.map((benefit, index) => (
                            <Tr 
                              key={index}
                              fontWeight={benefit.isTotal ? "bold" : "normal"}
                              bg={benefit.isTotal ? `${methodistGreen}10` : undefined}
                            >
                              <Td>{benefit.category}</Td>
                              <Td isNumeric color={benefit.year1 < 0 ? "red.500" : undefined}>
                                {benefit.year1 < 0 ? "-" : ""}
                                ${Math.abs(benefit.year1).toLocaleString()}
                              </Td>
                              <Td isNumeric color={benefit.year2 < 0 ? "red.500" : undefined}>
                                {benefit.year2 < 0 ? "-" : ""}
                                ${Math.abs(benefit.year2).toLocaleString()}
                              </Td>
                              <Td isNumeric color={benefit.year3 < 0 ? "red.500" : undefined}>
                                {benefit.year3 < 0 ? "-" : ""}
                                ${Math.abs(benefit.year3).toLocaleString()}
                              </Td>
                              <Td isNumeric color={benefit.total < 0 ? "red.500" : undefined}>
                                {benefit.total < 0 ? "-" : ""}
                                ${Math.abs(benefit.total).toLocaleString()}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                    
                    <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                      <Box>
                        <Text fontSize="xs" color="blue.700">Total Investment</Text>
                        <Text fontSize="md" fontWeight="bold" color={methodistBlue}>$5,000</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="blue.700">Total 3-Year Benefits</Text>
                        <Text fontSize="md" fontWeight="bold" color={methodistGreen}>$22,500</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="blue.700">Net Benefit</Text>
                        <Text fontSize="md" fontWeight="bold" color={methodistGreen}>$17,500</Text>
                      </Box>
                    </Grid>
                  </Box>
                  
                  {/* Non-Financial Benefits */}
                  <Box>
                    <Heading size="sm" mb={4} color={methodistBlue}>
                      Non-Financial Benefits
                    </Heading>
                    <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} gap={4}>
                      {nonFinancialBenefits.map((benefit, index) => (
                        <Box 
                          key={index}
                          bg={bgColor} 
                          p={4} 
                          borderRadius="lg" 
                          boxShadow="md" 
                          borderWidth="1px" 
                          borderColor={borderColor}
                        >
                          <Flex align="center" mb={2}>
                            <Circle size="32px" bg={`${benefit.color}15`} mr={3}>
                              <Icon as={benefit.icon} color={benefit.color} boxSize={4} />
                            </Circle>
                            <Text fontWeight="medium" fontSize="sm">{benefit.metric}</Text>
                          </Flex>
                          <Text fontSize="xl" fontWeight="bold" mb={1} color={benefit.color}>
                            {benefit.value}
                          </Text>
                          <Text fontSize="xs" color={textColor}>
                            {benefit.impact}
                          </Text>
                        </Box>
                      ))}
                    </Grid>
                  </Box>
                </Flex>
              </TabPanel>
              
              {/* 5-Year Projections Tab */}
              <TabPanel p={0}>
                <VStack
                  spacing={4}
                  align="stretch"
                  overflowY="auto"
                  maxH="calc(100vh - 200px)"
                  pr={4}
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      width: '6px',
                      background: '#F7FAFC',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#CBD5E0',
                      borderRadius: '24px',
                    },
                  }}
                >
                  <Heading size="sm" mb={4} color={methodistBlue}>
                    5-Year ROI Projection Scenarios
                  </Heading>
                  
                  {/* Key Assumptions Alert */}
                  <Alert status="info" mb={4} borderRadius="md" bg="blue.50">
                    <AlertIcon color={methodistBlue} />
                    <VStack align="start" spacing={1} fontSize="xs" width="100%">
                      <Text fontWeight="bold" color={methodistBlue}>Key Analysis Assumptions:</Text>
                      <Grid templateColumns="repeat(3, 1fr)" width="100%" gap={2}>
                        <HStack>
                          <Icon as={Users} size={3} color={methodistBlue} />
                          <Text>Total potential users: 3,000</Text>
                        </HStack>
                        <HStack>
                          <Icon as={DollarSign} size={3} color={methodistBlue} />
                          <Text>Average physician salary: ${formatCurrency(300000)}</Text>
                        </HStack>
                        <HStack>
                          <Icon as={DollarSign} size={3} color={methodistBlue} />
                          <Text>Cost per burnout: ${formatCurrency(750000)} (2.5x salary)</Text>
                        </HStack>
                        <HStack>
                          <Icon as={Percent} size={3} color={methodistBlue} />
                          <Text>Baseline burnout rate: 54% (33% severe impact)</Text>
                        </HStack>
                        <HStack>
                          <Icon as={DollarSign} size={3} color={methodistBlue} />
                          <Text>One-time implementation: $5,000</Text>
                        </HStack>
                        <HStack>
                          <Icon as={DollarSign} size={3} color={methodistBlue} />
                          <Text>Annual maintenance: $1,000/yr</Text>
                        </HStack>
                        <HStack>
                          <Icon as={Activity} size={3} color={methodistBlue} />
                          <Text>Burnout reduction: 20%</Text>
                        </HStack>
                      </Grid>
                    </VStack>
                  </Alert>
                  
                  {/* 5-Year Graph */}
                  <Box 
                    bg={bgColor} 
                    borderRadius="lg" 
                    boxShadow="md" 
                    p={4} 
                    borderWidth="1px" 
                    borderColor={borderColor}
                    mb={4}
                  >
                    {/* Interactive Simulation Controls */}
                    <Grid templateColumns={["1fr", "1fr 1fr 1fr"]} gap={4} mb={4}>
                      <FormControl>
                        <FormLabel fontSize="xs">Adoption Rate: {adoptionRate}%</FormLabel>
                        <Slider
                          min={1}
                          max={99}
                          step={1}
                          value={adoptionRate}
                          onChange={(val) => setAdoptionRate(val)}
                          colorScheme="blue"
                        >
                          <SliderTrack>
                            <SliderFilledTrack />
                          </SliderTrack>
                          <SliderThumb boxSize={4} />
                        </Slider>
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel fontSize="xs">Burnout Reduction: {burnoutReduction}%</FormLabel>
                        <Slider
                          min={5}
                          max={50}
                          step={1}
                          value={burnoutReduction}
                          onChange={(val) => setBurnoutReduction(val)}
                          colorScheme="green"
                        >
                          <SliderTrack>
                            <SliderFilledTrack />
                          </SliderTrack>
                          <SliderThumb boxSize={4} />
                        </Slider>
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel fontSize="xs">Platform Cost: ${platformCost.toLocaleString()} (one-time implementation)</FormLabel>
                        <Slider
                          min={1000}
                          max={20000}
                          step={1000}
                          value={platformCost}
                          onChange={(val) => setPlatformCost(val)}
                          colorScheme="purple"
                        >
                          <SliderTrack>
                            <SliderFilledTrack />
                          </SliderTrack>
                          <SliderThumb boxSize={4} />
                        </Slider>
                      </FormControl>
                    </Grid>
                    
                    <HStack mt={2} mb={4} p={3} bg="blue.50" borderRadius="md">
                      <Icon as={Info} color="blue.500" />
                      <Text fontSize="xs">Adjust parameters to see real-time Monte Carlo simulation results based on 3,000 potential users.</Text>
                    </HStack>
                    
                    <Box h="400px" position="relative" mt={2}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={chartData}
                          margin={{
                            top: 20,
                            right: 30,
                            left: 30,
                            bottom: 20,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis 
                            dataKey="name" 
                            stroke="#718096"
                            fontSize={12}
                          />
                          <YAxis
                            stroke="#718096"
                            fontSize={12}
                            tickFormatter={(value) => `$${value}K`}
                            domain={[0, maxValue]}
                            padding={{ top: 20 }}
                          />
                          <RechartsTooltip
                            formatter={(value, name) => [`$${value}K`, name]}
                            labelFormatter={(label) => `${label}`}
                          />
                          {adoptionScenarios.map((scenario, index) => (
                            <Line
                              key={scenario.name}
                              type="monotone"
                              dataKey={scenario.name}
                              stroke={scenario.color}
                              strokeWidth={3}
                              strokeDasharray={scenario.strokeDasharray}
                              dot={{
                                fill: "white",
                                stroke: scenario.color,
                                strokeWidth: 2,
                                r: 4
                              }}
                              activeDot={{
                                fill: scenario.color,
                                stroke: "white",
                                strokeWidth: 2,
                                r: 6
                              }}
                              animationDuration={1000}
                              animationBegin={index * 200}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                    
                    {/* Legend */}
                    <Box mt={8} p={3} borderRadius="md" bg="gray.50">
                      <Text fontSize="xs" fontWeight="medium" mb={2}>Adoption Rate Scenarios (% of 3,000 potential users):</Text>
                      <Flex justify="space-between" wrap="wrap">
                        {adoptionScenarios.map((scenario, index) => (
                          <HStack key={index} mb={2} width={"30%"}>
                            <Box w="16px" h="4px" bg={scenario.color} borderRadius="full" />
                            <Text fontSize="xs">{scenario.name}</Text>
                            <Tooltip hasArrow bg={scenario.color} color="white" label={`Year 1: ${scenario.rates[0]}% → Year 5: ${scenario.rates[4]}%`}>
                              <Circle size="16px" bg={`${scenario.color}20`} cursor="help">
                                <Icon as={Info} color={scenario.color} boxSize={2.5} />
                              </Circle>
                            </Tooltip>
                          </HStack>
                        ))}
                      </Flex>
                    </Box>
                  </Box>
                  
                  {/* Monte Carlo Simulation Results */}
                  <Box bg={bgColor} borderRadius="lg" boxShadow="md" p={4} borderWidth="1px" borderColor={borderColor} mb={4}>
                    <Flex justify="space-between" align="center" mb={3}>
                      <Heading size="sm" color={methodistBlue}>
                        Monte Carlo Simulation: Adoption Rate Scenarios
                      </Heading>
                      <Tooltip label="Results based on 1,000 simulations with varying parameters including adoption rates, burnout reduction efficacy, and cost of physician turnover">
                        <Circle size="24px" bg={`${methodistBlue}15`} cursor="help">
                          <Icon as={Info} color={methodistBlue} boxSize={3} />
                        </Circle>
                      </Tooltip>
                    </Flex>
                    
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th fontSize="xs">Scenario</Th>
                          <Th isNumeric fontSize="xs">Cost Savings</Th>
                          <Th isNumeric fontSize="xs">ROI</Th>
                          <Th isNumeric fontSize="xs">Doctors Saved</Th>
                          <Th isNumeric fontSize="xs">5-Year Savings</Th>
                          <Th isNumeric fontSize="xs">ROI %</Th>
                          <Th isNumeric fontSize="xs">Confidence</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {simulationResults.map((result, index) => (
                          <Tr 
                            key={index} 
                            bg={index === 1 ? "blue.50" : "transparent"}
                            fontWeight={index === 1 ? "medium" : "normal"}
                          >
                            <Td fontSize="xs">{result.name}</Td>
                            <Td isNumeric fontSize="xs">{result.results.costSavings.toLocaleString()}</Td>
                            <Td isNumeric fontSize="xs">{result.results.roi}%</Td>
                            <Td isNumeric fontSize="xs">{result.results.doctorsSaved}</Td>
                            <Td isNumeric fontSize="xs">{formatCurrency(result.results.fiveYearSavings)}</Td>
                            <Td isNumeric fontSize="xs" color={methodistGreen} fontWeight="bold">
                              {formatPercentage(result.results.roi)}
                            </Td>
                            <Td isNumeric fontSize="xs">{result.results.confidenceInterval}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  
                  {/* Cost Comparison Alert */}
                  <Alert status="info" mb={4} borderRadius="md">
                    <AlertIcon />
                    <Box fontSize="sm">
                      <Text fontWeight="bold">Cost Efficiency:</Text> Our $5,000 solution vs. $50-100K for commercial equivalents
                      <Text fontSize="xs" mt={1}>Based on market research of similar custom healthcare platforms</Text>
                    </Box>
                  </Alert>
                  
                  <Alert
                    status="info"
                    variant="subtle"
                    borderRadius="md"
                    mt={4}
                  >
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Platform Cost Assumption</AlertTitle>
                      <Text fontSize="sm">Initial platform cost: ${formatCurrency(platformCost)}</Text>
                      <Text fontSize="xs" mt={1}>Based on market research of similar custom healthcare platforms</Text>
                    </Box>
                  </Alert>
                  
                  <Box pt={8} borderTop="1px" borderColor="gray.200">
                    <Text fontSize="sm" color="gray.600" fontWeight="medium">
                      Sources and Methodology:
                    </Text>
                    <UnorderedList fontSize="sm" color="gray.600" spacing={2} pl={4} mt={2}>
                      <ListItem>
                        Burnout rates based on 2023 Medscape National Physician Burnout & Depression Report
                      </ListItem>
                      <ListItem>
                        Cost calculations consider both direct turnover costs (2-3x annual salary) and productivity impact (30-50% reduction)
                      </ListItem>
                      <ListItem>
                        33% severe impact rate represents cases leading to turnover or significant productivity loss, based on longitudinal studies
                      </ListItem>
                      <ListItem>
                        Conservative estimate using 2.5x salary multiplier for total burnout cost (including recruitment, onboarding, and lost revenue)
                      </ListItem>
                    </UnorderedList>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </SlideContainer>
    </MethodistSlide>
  );
};

export default ROISlide;
